// Phase-aware TanStack Query polling for a docker_operation.
// Polls every POLL_INTERVAL_MS.default by default; switches cadence
// during phases that need faster updates (pulling/stopping/awaiting_healthy).

import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { POLL_INTERVAL_MS } from './tokens'
import type { DockerOperation } from './types'

interface Args {
  tenantId: number
  instanceId: string
  operationId: string | null
}

export function useOperationPolling({ tenantId, instanceId, operationId }: Args) {
  return useQuery<DockerOperation>({
    queryKey: ['docker-operation', tenantId, instanceId, operationId],
    queryFn: async () => {
      const res = await api.get<DockerOperation>(
        `/v1/tenants/${tenantId}/inventory/ec2/${instanceId}/docker/operations/${operationId}`,
      )
      const data = res.data
      // Defensive: legacy corrupted rows may have phases serialized as `{}`
      // instead of `[]` (pre-fix bug where Python list was passed to JSONB
      // without ::jsonb cast). Coerce so downstream `find`/`for-of` don't blow up.
      if (!Array.isArray(data.phases)) {
        data.phases = []
      }
      return data
    },
    enabled: !!operationId,
    refetchInterval: query => {
      const op = query.state.data as DockerOperation | undefined
      if (!op) return POLL_INTERVAL_MS.default
      if (op.status !== 'running') return false // stop polling on terminal state
      const phase = op.current_phase
      if (phase === 'pulling') return POLL_INTERVAL_MS.pulling
      if (phase === 'stopping') return POLL_INTERVAL_MS.stopping
      if (phase === 'awaiting_healthy') return POLL_INTERVAL_MS.awaiting_healthy
      return POLL_INTERVAL_MS.default
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
  })
}
